// Shared sanitizer for the Command Center briefing/next-meeting narrative HTML.
// The backend already restricts this HTML (validateBriefingHtml), but we re-sanitize
// on the client before dangerouslySetInnerHTML to keep the trusted tag/class subset.
const allowedHtmlTags = new Set(["BR", "SPAN", "STRONG"]);
const allowedSpanClasses = new Set(["briefing-action", "briefing-muted"]);

export const escapeHtml = (value) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

// Re-renders the restricted HTML, dropping any unsupported tags/attributes and
// wrapping the first sentence in a .briefing-lead span for emphasis.
export const sanitizeBriefingHtml = (html) => {
  if (!html) return "";
  if (typeof window === "undefined" || typeof window.DOMParser !== "function") {
    return escapeHtml(html);
  }

  const parser = new window.DOMParser();
  const parsed = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const source = parsed.body.firstElementChild;
  const output = window.document.createElement("div");

  const sanitizeNode = (node) => {
    if (node.nodeType === window.Node.TEXT_NODE) {
      return window.document.createTextNode(node.textContent || "");
    }

    if (node.nodeType !== window.Node.ELEMENT_NODE) {
      return window.document.createDocumentFragment();
    }

    const tagName = node.tagName;
    if (!allowedHtmlTags.has(tagName)) {
      const fragment = window.document.createDocumentFragment();
      node.childNodes.forEach((childNode) => {
        fragment.appendChild(sanitizeNode(childNode));
      });
      return fragment;
    }

    const element = window.document.createElement(tagName.toLowerCase());
    if (tagName === "SPAN") {
      const allowedClass = Array.from(node.classList).find((className) =>
        allowedSpanClasses.has(className)
      );
      if (allowedClass) element.className = allowedClass;
    }

    node.childNodes.forEach((childNode) => {
      element.appendChild(sanitizeNode(childNode));
    });
    return element;
  };

  source?.childNodes.forEach((node) => {
    output.appendChild(sanitizeNode(node));
  });

  const walker = window.document.createTreeWalker(
    output,
    window.NodeFilter.SHOW_TEXT
  );
  let textNode = walker.nextNode();

  while (textNode) {
    const text = textNode.textContent || "";
    const periodIndex = text.indexOf(".");

    if (periodIndex !== -1) {
      const fragment = window.document.createDocumentFragment();
      const lead = window.document.createElement("span");
      lead.className = "briefing-lead";
      lead.textContent = text.slice(0, periodIndex + 1);
      fragment.appendChild(lead);

      const rest = text.slice(periodIndex + 1);
      if (rest) fragment.appendChild(window.document.createTextNode(rest));

      textNode.parentNode.replaceChild(fragment, textNode);
      break;
    }

    textNode = walker.nextNode();
  }

  return output.innerHTML;
};
